import { useEffect, useState } from "react";
import LoadingIcon from "@common/LoadingIcon";
import { BlankContract } from "@templates/BlankContractData";
import { CONTRACT } from "@types/contract";
import { error_message } from "@constants/Errors";
import { APP_NAMES, CONTRACT_STATUS } from "@constants/Enums";
import { sortApps } from "@services/Utilities";

type DisplayStatusProps = {
  apps: any[];
  contract: CONTRACT;
  updateContractData: Function;
  readOnly: boolean;
};

const DisplayStatus = ({
  apps,
  contract,
  updateContractData,
  readOnly = true,
}: DisplayStatusProps) => {
  const isYobelyLicense = contract.app_name?.toUpperCase() === APP_NAMES.YOBELY;
  const contractStatusLabels: Record<CONTRACT_STATUS, string> = {
    [CONTRACT_STATUS.ACTIVE]: "契約中",
    [CONTRACT_STATUS.INACTIVE]: "未契約",
    [CONTRACT_STATUS.EXPIRED]: "契約終了",
    [CONTRACT_STATUS.NO_CONTRACT]: "未契約",
    [CONTRACT_STATUS.CANCELLED]: "解約済み",
  };

  const app: any = apps.find((app) => app.id === contract.app_id);
  const appName: any = app
    ? app.name
    : error_message.tenant.unknown_app(contract.app_id);

  return (
    <tr key={contract.app_id}>
      <td
        className="py-4 whitespace-nowrap text-xl font-medium text-gray-900 w-1/5"
        title={app ? app.description : ""}
      >
        {appName}
      </td>
      <td>
        <div className="mb-2 mt-1 text-xl text-black">
          <label className="flex items-center">
            <select
              className={`dropdown-bar min-w-30 ${
                readOnly ? "appearance-none hover:cursor-not-allowed" : ""
              }`}
              value={contract.status}
              onChange={(e) =>
                updateContractData(contract.app_id, "status", e.target.value)
              }
              disabled={readOnly}
            >
              {Object.values(CONTRACT_STATUS).map((status) => (
                <option key={status} value={status}>
                  {contractStatusLabels[status]}
                </option>
              ))}
            </select>
          </label>
        </div>
      </td>
      <td>
        <div className="flex items-center mt-1">
          <input
            type="date"
            value={contract.start_date?.split("T")[0]}
            min="2020-01-01"
            max="2099-12-31"
            onChange={(e) =>
              updateContractData(contract.app_id, "start_date", e.target.value)
            }
            disabled={readOnly}
            className={`${
              readOnly ? "hover:cursor-not-allowed" : ""
            } text-title-md text-black mb-2 w-50 border border-text-field-border rounded-xl p-2`}
          />
          <span className="mx-2">~</span>
          <input
            type="date"
            value={contract.end_date?.split("T")[0]}
            onChange={(e) =>
              updateContractData(contract.app_id, "end_date", e.target.value)
            }
            disabled={readOnly}
            className={`${
              readOnly ? "hover:cursor-not-allowed" : ""
            } text-title-md text-black mb-2 w-50 border border-text-field-border rounded-xl p-2`}
          />
        </div>
      </td>
      <td>
        <div className="ml-5 flex items-center">
          <input
            className={`${
              readOnly ? "hover:cursor-not-allowed" : ""
            } form-text-field-border-rounded-xl min-w-30 border`}
            type={isYobelyLicense || readOnly ? "text" : "number"}
            value={isYobelyLicense ? "ー" : contract.contract_count}
            min="0"
            disabled={isYobelyLicense || readOnly}
            onChange={(e) =>
              updateContractData(
                contract.app_id,
                "contract_count",
                e.target.value,
              )
            }
          />
        </div>
      </td>
      <td>
        <div className="ml-5 flex items-center">
          <input
            className={`${
              readOnly ? "hover:cursor-not-allowed" : ""
            } form-text-field-border-rounded-xl border`}
            type={readOnly ? "text" : "number"}
            value={contract.unit_price}
            min="0"
            disabled={readOnly}
            onChange={(e) =>
              updateContractData(contract.app_id, "unit_price", e.target.value)
            }
          />
          <span className="ml-5 text-xl">円</span>
        </div>
      </td>
    </tr>
  );
};

const CreateTenantLicenseTable = ({
  contractData,
  setContractData,
  updateContractData,
  appData,
  readOnly = true,
}) => {
  const [error, setError] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(true);

  const [isSorted, setIsSorted] = useState(false);

  useEffect(() => {
    if (!contractData || contractData.length === 0 || isSorted) {
      setLoading(false);
      return;
    }

    const hasInvalidTenant = contractData.some(
      (contract) => contract.tenant_id === 0,
    );
    if (hasInvalidTenant) {
      return;
    }

    const appsOrder = [APP_NAMES.KAKELY, APP_NAMES.YOBELY, APP_NAMES.WORKJOY];
    const sorted = [...contractData].sort((a, b) => {
      const indexA = appsOrder.indexOf(a.app_name);
      const indexB = appsOrder.indexOf(b.app_name);
      return (indexA === -1 ? 999 : indexA) - (indexB === -1 ? 999 : indexB);
    });

    setContractData(sorted);
    setIsSorted(true);
    setLoading(false);
  }, [contractData, isSorted]);

  useEffect(() => {
    const createBlankContracts = (appData) => {
      return appData.map((app) => ({
        ...BlankContract,
        app_id: app.id,
      }));
    };

    const blankContracts = createBlankContracts(appData);
    if (contractData.length <= 0) {
      setContractData(blankContracts);
    }
  }, [appData]);

  return (
    <div className="overflow-x-auto">
      {loading ? (
        <LoadingIcon />
      ) : error ? (
        <p className="text-red-500">Error: {error}</p>
      ) : appData.length > 1 ? (
        <table className="min-w-full divide-y divide-mayo-divider-dark table-fixed">
          <thead>
            <tr>
              <th
                scope="col"
                className="pb-2 pt-3 text-left text-lg font-medium text-font-label-blue uppercase min-w-[150px]"
              >
                アプリ
              </th>
              <th
                scope="col"
                className="pb-2 pt-3 text-left text-lg font-medium text-font-label-blue uppercase min-w-[160px]"
              >
                契約状態
              </th>
              <th
                scope="col"
                className="pb-2 pt-3 text-left text-lg font-medium text-font-label-blue uppercase min-w-[150px]"
              >
                契約期間
              </th>
              <th
                scope="col"
                className="pl-3 pb-2 pt-3 text-left text-lg font-medium text-font-label-blue uppercase min-w-[160px]"
              >
                ライセンス数
              </th>
              <th
                scope="col"
                className="pl-3 pb-2 pt-3 text-left text-lg font-medium text-font-label-blue uppercase min-w-[250px]"
              >
                単価
              </th>
            </tr>
          </thead>
          <tbody>
            {sortApps(contractData).map((contract) => (
              <DisplayStatus
                apps={appData}
                contract={contract}
                key={contract.app_id}
                updateContractData={updateContractData}
                readOnly={readOnly}
              />
            ))}
          </tbody>
        </table>
      ) : (
        <h1>{error_message.tenant.no_contract}</h1>
      )}
    </div>
  );
};

export default CreateTenantLicenseTable;
